import React from 'react';
import Icon from '../../../components/AppIcon';

const PrivacyStatusIndicator = ({ privacyStatus }) => {
  const statusAreas = [
    {
      id: 'selective-disclosure',
      name: 'Selective Disclosure',
      status: privacyStatus?.selectiveDisclosure || 'active',
      description: 'Attribute sharing controls',
      icon: 'Shield'
    },
    {
      id: 'zero-knowledge',
      name: 'Zero-Knowledge Proofs',
      status: privacyStatus?.zeroKnowledge || 'active',
      description: 'Privacy-preserving verification',
      icon: 'Eye'
    },
    {
      id: 'key-management',
      name: 'Key Management',
      status: privacyStatus?.keyManagement || 'secure',
      description: 'Cryptographic key security',
      icon: 'Key'
    },
    {
      id: 'data-retention',
      name: 'Data Retention',
      status: privacyStatus?.dataRetention || 'optimized',
      description: 'Automated data cleanup',
      icon: 'Clock'
    },
    {
      id: 'blockchain-privacy',
      name: 'Blockchain Privacy',
      status: privacyStatus?.blockchainPrivacy || 'protected',
      description: 'Transaction privacy controls',
      icon: 'Link'
    },
    {
      id: 'consent-management',
      name: 'Consent Management',
      status: privacyStatus?.consentManagement || 'active',
      description: 'Data sharing agreements',
      icon: 'Users'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case'secure': case'optimized': case'protected': return 'text-success';
      case 'warning': case'attention': return 'text-warning';
      case 'inactive': case'vulnerable': case'expired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': case'secure': case'optimized': case'protected': return 'CheckCircle';
      case 'warning': case'attention': return 'AlertTriangle';
      case 'inactive': case'vulnerable': case'expired': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'secure': return 'Secure';
      case 'optimized': return 'Optimized';
      case 'protected': return 'Protected';
      case 'warning': return 'Warning';
      case 'attention': return 'Needs Attention';
      case 'inactive': return 'Inactive';
      case 'vulnerable': return 'Vulnerable';
      case 'expired': return 'Expired';
      default: return 'Unknown';
    }
  };

  const getOverallPrivacyScore = () => {
    const scores = {
      'active': 100, 'secure': 100, 'optimized': 100, 'protected': 100,
      'warning': 70, 'attention': 70,
      'inactive': 30, 'vulnerable': 30, 'expired': 30
    };
    
    const statusValues = Object.values(privacyStatus || {});
    if (statusValues?.length === 0) return 85;
    
    const totalScore = statusValues?.reduce((sum, status) => sum + (scores?.[status] || 50), 0);
    return Math.round(totalScore / statusValues?.length);
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return 'ShieldCheck';
    if (score >= 70) return 'Shield';
    return 'ShieldAlert';
  };

  const overallScore = getOverallPrivacyScore();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Privacy Protection Status</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time privacy status indicators across all system areas
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <div className="text-2xl font-bold text-foreground">{overallScore}%</div>
            <div className="text-xs text-muted-foreground">Privacy Score</div>
          </div>
          <Icon 
            name={getScoreIcon(overallScore)} 
            size={32} 
            className={getScoreColor(overallScore)} 
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statusAreas?.map((area) => (
          <div key={area?.id} className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name={area?.icon} size={16} className="text-primary" />
                <h4 className="font-medium text-sm text-foreground">{area?.name}</h4>
              </div>
              <div className="flex items-center space-x-1">
                <Icon 
                  name={getStatusIcon(area?.status)} 
                  size={14} 
                  className={getStatusColor(area?.status)} 
                />
                <span className={`text-xs font-medium ${getStatusColor(area?.status)}`}>
                  {getStatusLabel(area?.status)}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{area?.description}</p>
          </div>
        ))}
      </div>
      {/* Privacy Recommendations */}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <h4 className="font-medium text-foreground mb-3 flex items-center">
          <Icon name="Lightbulb" size={16} className="text-primary mr-2" />
          Privacy Recommendations
        </h4>
        <div className="space-y-2 text-sm">
          {overallScore < 90 && (
            <div className="flex items-start space-x-2">
              <Icon name="ArrowRight" size={14} className="text-primary mt-0.5" />
              <span className="text-muted-foreground">
                Enable zero-knowledge proofs by default for maximum privacy protection
              </span>
            </div>
          )}
          {privacyStatus?.dataRetention !== 'optimized' && (
            <div className="flex items-start space-x-2">
              <Icon name="ArrowRight" size={14} className="text-primary mt-0.5" />
              <span className="text-muted-foreground">
                Configure automatic data retention policies to minimize your digital footprint
              </span>
            </div>
          )}
          {privacyStatus?.keyManagement !== 'secure' && (
            <div className="flex items-start space-x-2">
              <Icon name="ArrowRight" size={14} className="text-primary mt-0.5" />
              <span className="text-muted-foreground">
                Set up automatic key rotation for enhanced cryptographic security
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Last Updated */}
      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
        <span>Privacy status updated in real-time</span>
        <span>Last checked: {new Date()?.toLocaleTimeString()}</span>
      </div>
    </div>
  );
};

export default PrivacyStatusIndicator;